// SemenInventory.js — Semen Inventory module (Reproduction clinic).
// Roadmap 3.2.0. Batch + ledger UI over the /semen API. All calls go through
// the central services/api instance (token + X-ROLE-ID applied automatically).
//
// Screens covered here: stock list (+ filters), add/edit batch, batch card with
// its movements, movement dialog (receive/reserve/release/use/dispose/adjust),
// and the alerts panel (near-expiry / low-stock / no storage location).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Alert, Tooltip, Chip, Stack, TableContainer, InputAdornment, CircularProgress,
  FormControlLabel, Switch, Divider, Collapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
  smartActionButtonSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';
import SettingsPageHeader from '../../components/SettingsPageHeader';

const SEMEN_TYPES = ['Fresh', 'Chilled', 'Frozen'];
const STATUSES = ['Available', 'Reserved', 'Used', 'Expired', 'Disposed'];
const MOVEMENT_TYPES = ['Receive', 'Reserve', 'Release', 'Use', 'Dispose', 'Adjust'];

const COPY = {
  ar: {
    title: 'مخزون السائل المنوي',
    subtitle: 'دفعات السائل المنوي وحركاتها (استلام/حجز/استخدام/إتلاف) داخل عيادة الإنتاج.',
    search: 'بحث برقم الدفعة أو اسم الفحل…',
    addBatch: 'إضافة دفعة',
    refresh: 'تحديث',
    all: 'الكل',
    lowStock: 'رصيد منخفض',
    nearExpiry: 'قرب الانتهاء',
    noData: 'لا توجد دفعات',
    stallion: 'الفحل', sourceType: 'المصدر', semenType: 'النوع', batchNo: 'رقم الدفعة',
    collectionDate: 'تاريخ الجمع', receiveDate: 'تاريخ الاستلام', expiryDate: 'تاريخ الانتهاء',
    total: 'الكلي', used: 'المستخدم', reserved: 'المحجوز', remaining: 'المتبقي',
    storage: 'موقع التخزين', tank: 'الخزان', canister: 'الكانيستر', cane: 'القصبة', goblet: 'الكوب',
    status: 'الحالة', notes: 'ملاحظات', actions: 'إجراءات', doses: 'جرعات',
    edit: 'تعديل', del: 'حذف', save: 'حفظ', cancel: 'إلغاء', close: 'إغلاق',
    addBatchTitle: 'إضافة دفعة جديدة', editBatchTitle: 'تعديل بيانات الدفعة',
    initialDoses: 'الجرعات الأولية',
    batchCard: 'بطاقة الدفعة', movements: 'الحركات', addMovement: 'حركة جديدة', noMovements: 'لا حركات بعد',
    movementType: 'نوع الحركة', quantity: 'الكمية (جرعات)', caseId: 'رقم الحالة', inseminationId: 'رقم التلقيح',
    reason: 'السبب', fromReservation: 'خصم من المحجوز', movementDate: 'تاريخ الحركة',
    confirmDelete: 'تأكيد حذف الدفعة؟', deleteHint: 'لا يمكن الحذف إن وُجدت جرعات مستخدمة أو محجوزة — استخدم الإتلاف.',
    alerts: 'تنبيهات', alertsNearExpiry: 'قرب الانتهاء', alertsLowStock: 'رصيد منخفض', alertsNoStorage: 'بلا موقع تخزين',
    noAlerts: 'لا تنبيهات حالية', required: 'حقل مطلوب', saved: 'تم الحفظ', batchNoRequired: 'رقم الدفعة مطلوب',
    movementTypes: { Receive: 'استلام', Reserve: 'حجز', Release: 'فك حجز', Use: 'استخدام', Dispose: 'إتلاف', Adjust: 'تعديل رصيد' },
    statuses: { Available: 'متاح', Reserved: 'محجوز', Used: 'مُستخدم', Expired: 'منتهٍ', Disposed: 'مُتلف' },
    sources: { 'In-house': 'داخلي', External: 'خارجي' },
  },
  en: {
    title: 'Semen Inventory',
    subtitle: 'Semen batches and their movements (receive/reserve/use/dispose) inside the Reproduction clinic.',
    search: 'Search by batch no. or stallion…',
    addBatch: 'Add batch',
    refresh: 'Refresh',
    all: 'All',
    lowStock: 'Low stock',
    nearExpiry: 'Near expiry',
    noData: 'No batches',
    stallion: 'Stallion', sourceType: 'Source', semenType: 'Type', batchNo: 'Batch no.',
    collectionDate: 'Collection date', receiveDate: 'Receive date', expiryDate: 'Expiry date',
    total: 'Total', used: 'Used', reserved: 'Reserved', remaining: 'Remaining',
    storage: 'Storage location', tank: 'Tank', canister: 'Canister', cane: 'Cane', goblet: 'Goblet',
    status: 'Status', notes: 'Notes', actions: 'Actions', doses: 'doses',
    edit: 'Edit', del: 'Delete', save: 'Save', cancel: 'Cancel', close: 'Close',
    addBatchTitle: 'Add new batch', editBatchTitle: 'Edit batch',
    initialDoses: 'Initial doses',
    batchCard: 'Batch card', movements: 'Movements', addMovement: 'New movement', noMovements: 'No movements yet',
    movementType: 'Movement type', quantity: 'Quantity (doses)', caseId: 'Case ID', inseminationId: 'Insemination ID',
    reason: 'Reason', fromReservation: 'Deduct from reserved', movementDate: 'Movement date',
    confirmDelete: 'Delete this batch?', deleteHint: 'Cannot delete when doses are used or reserved — use Dispose instead.',
    alerts: 'Alerts', alertsNearExpiry: 'Near expiry', alertsLowStock: 'Low stock', alertsNoStorage: 'No storage location',
    noAlerts: 'No current alerts', required: 'Required field', saved: 'Saved', batchNoRequired: 'Batch no. is required',
    movementTypes: { Receive: 'Receive', Reserve: 'Reserve', Release: 'Release', Use: 'Use', Dispose: 'Dispose', Adjust: 'Adjust' },
    statuses: { Available: 'Available', Reserved: 'Reserved', Used: 'Used', Expired: 'Expired', Disposed: 'Disposed' },
    sources: { 'In-house': 'In-house', External: 'External' },
  },
};

const statusColor = (s) => ({
  Available: { c: COLORS.green, b: COLORS.greenSoft },
  Reserved: { c: COLORS.gold, b: COLORS.goldSoft },
  Used: { c: COLORS.blue, b: COLORS.blueSoft },
  Expired: { c: COLORS.warning, b: COLORS.warningSoft },
  Disposed: { c: COLORS.rose, b: COLORS.roseSoft },
}[s] || { c: COLORS.muted, b: COLORS.cardSoft });

const emptyBatch = {
  stallion_id: '', stallion_name: '', source_type: 'In-house', semen_type: 'Frozen', batch_no: '',
  collection_date: '', receive_date: '', expiry_date: '', doses_total: 0,
  storage_tank: '', storage_canister: '', storage_cane: '', storage_goblet: '', notes: '',
};

const SemenInventory = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const text = COPY[lang];
  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  const [rows, setRows] = useState([]);
  const [stallions, setStallions] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [showAlerts, setShowAlerts] = useState(true);

  // filters
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [lowStock, setLowStock] = useState(false);
  const [nearExpiry, setNearExpiry] = useState(false);

  // batch add/edit dialog
  const [batchOpen, setBatchOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [batchForm, setBatchForm] = useState(emptyBatch);

  // batch card dialog
  const [cardOpen, setCardOpen] = useState(false);
  const [card, setCard] = useState(null);

  // movement dialog
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveForm, setMoveForm] = useState({ movement_type: 'Receive', quantity: 1, case_id: '', insemination_id: '', reason: '', notes: '', from_reservation: false, movement_date: '' });

  // delete dialog
  const [delTarget, setDelTarget] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.semen_type = typeFilter;
      if (lowStock) params.low_stock = 1;
      if (nearExpiry) params.near_expiry = 1;
      const res = await api.get('/semen/batches', { params });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setErr(e.response?.data?.error || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter, typeFilter, lowStock, nearExpiry]);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await api.get('/semen/alerts');
      setAlerts(res.data);
    } catch (_) { /* non-fatal */ }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);
  useEffect(() => {
    api.get('/stallionslist').then((r) => setStallions(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const alertCount = useMemo(() => {
    if (!alerts) return 0;
    return (alerts.near_expiry?.length || 0) + (alerts.low_stock?.length || 0) + (alerts.no_storage_location?.length || 0);
  }, [alerts]);

  // ---- batch dialog ----
  const openAdd = () => { setEditing(null); setBatchForm(emptyBatch); setBatchOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setBatchForm({
      stallion_id: b.stallion_id || '', stallion_name: b.stallion_name || '', source_type: b.source_type || 'In-house',
      semen_type: b.semen_type || 'Frozen', batch_no: b.batch_no || '',
      collection_date: (b.collection_date || '').slice(0, 10), receive_date: (b.receive_date || '').slice(0, 10),
      expiry_date: (b.expiry_date || '').slice(0, 10), doses_total: b.doses_total ?? 0,
      storage_tank: b.storage_tank || '', storage_canister: b.storage_canister || '', storage_cane: b.storage_cane || '',
      storage_goblet: b.storage_goblet || '', notes: b.notes || '',
    });
    setBatchOpen(true);
  };

  const onPickStallion = (id) => {
    const s = stallions.find((x) => String(x.stallion_id) === String(id));
    setBatchForm((f) => ({ ...f, stallion_id: id, stallion_name: s?.name || f.stallion_name }));
  };

  const saveBatch = async () => {
    if (!batchForm.batch_no.trim()) { setErr(text.batchNoRequired); return; }
    setErr('');
    const payload = {
      ...batchForm,
      stallion_id: batchForm.stallion_id || null,
      doses_total: Number(batchForm.doses_total) || 0,
      collection_date: batchForm.collection_date || null,
      receive_date: batchForm.receive_date || null,
      expiry_date: batchForm.expiry_date || null,
    };
    try {
      if (editing) await api.put(`/semen/batches/${editing.batch_id}`, payload);
      else await api.post('/semen/batches', payload);
      setBatchOpen(false);
      await fetchRows();
      await fetchAlerts();
    } catch (e) {
      setErr(e.response?.data?.error || 'Save failed');
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/semen/batches/${delTarget.batch_id}`);
      setDelTarget(null);
      await fetchRows();
    } catch (e) {
      setErr(e.response?.data?.error || 'Delete failed');
    }
  };

  // ---- batch card ----
  const openCard = async (b) => {
    try {
      const res = await api.get(`/semen/batches/${b.batch_id}`);
      setCard(res.data);
      setCardOpen(true);
    } catch (e) {
      setErr(e.response?.data?.error || 'Load failed');
    }
  };
  const refreshCard = async (id) => {
    const res = await api.get(`/semen/batches/${id}`);
    setCard(res.data);
  };

  // ---- movement ----
  const openMovement = (type) => {
    setMoveForm({ movement_type: type, quantity: 1, case_id: '', insemination_id: '', reason: '', notes: '', from_reservation: false, movement_date: '' });
    setMoveOpen(true);
  };
  const saveMovement = async () => {
    setErr('');
    const m = moveForm;
    if ((m.movement_type === 'Dispose' || m.movement_type === 'Adjust') && !m.reason.trim()) { setErr(text.required); return; }
    const payload = {
      movement_type: m.movement_type,
      quantity: m.movement_type === 'Dispose' ? undefined : Number(m.quantity),
      case_id: m.case_id || null,
      insemination_id: m.insemination_id || null,
      reason: m.reason || null,
      notes: m.notes || null,
      from_reservation: !!m.from_reservation,
      movement_date: m.movement_date || undefined,
    };
    try {
      await api.post(`/semen/batches/${card.batch_id}/movements`, payload);
      setMoveOpen(false);
      await refreshCard(card.batch_id);
      await fetchRows();
      await fetchAlerts();
    } catch (e) {
      setErr(e.response?.data?.error || 'Movement failed');
    }
  };

  const fmtStorage = (b) => [b.storage_tank, b.storage_canister, b.storage_cane, b.storage_goblet].filter(Boolean).join(' / ') || '-';
  const StatusChip = ({ s }) => {
    const { c, b } = statusColor(s);
    return <Chip size="small" label={text.statuses[s] || s} sx={{ bgcolor: b, color: c, fontWeight: 800 }} />;
  };

  const needsQty = ['Receive', 'Reserve', 'Release', 'Use', 'Adjust'].includes(moveForm.movement_type);
  const needsReason = ['Dispose', 'Adjust'].includes(moveForm.movement_type);

  return (
    <Box className="smart-page-container" dir={dir} sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <SettingsPageHeader
        title={text.title}
        subtitle={text.subtitle}
        count={rows.length}
        actions={(
          <>
            <TextField
              size="small" placeholder={text.search} value={q}
              onChange={(e) => setQ(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
              sx={{ minWidth: { xs: '100%', sm: 260 } }}
            />
            <Tooltip title={text.refresh}>
              <IconButton onClick={() => { fetchRows(); fetchAlerts(); }} sx={{ color: COLORS.blue }}><RefreshIcon /></IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}
              sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}>
              {text.addBatch}
            </Button>
          </>
        )}
      />

      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}

      {/* Alerts panel */}
      <Paper elevation={0} sx={{ ...cardSx, mb: 2, p: 0, overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1.5, cursor: 'pointer' }} onClick={() => setShowAlerts((v) => !v)}>
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningAmberIcon sx={{ color: alertCount ? COLORS.warning : COLORS.muted }} />
            <Typography sx={{ fontWeight: 900, color: COLORS.text }}>{text.alerts}</Typography>
            <Chip size="small" label={alertCount} sx={{ bgcolor: alertCount ? COLORS.warningSoft : COLORS.cardSoft, color: alertCount ? COLORS.warning : COLORS.muted, fontWeight: 800, height: 22 }} />
          </Stack>
          <SwapVertIcon sx={{ color: COLORS.muted }} />
        </Stack>
        <Collapse in={showAlerts}>
          <Divider />
          <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            <AlertColumn title={text.alertsNearExpiry} items={alerts?.near_expiry} color={COLORS.warning} render={(x) => `${x.stallion_name || '-'} · ${x.batch_no} · ${(x.expiry_date || '').slice(0, 10)} · ${x.doses_remaining} ${text.doses}`} empty={text.noAlerts} />
            <AlertColumn title={text.alertsLowStock} items={alerts?.low_stock} color={COLORS.rose} render={(x) => `${x.stallion_name || '-'} · ${x.batch_no} · ${x.doses_remaining} ${text.doses}`} empty={text.noAlerts} />
            <AlertColumn title={text.alertsNoStorage} items={alerts?.no_storage_location} color={COLORS.blue} render={(x) => `${x.stallion_name || '-'} · ${x.batch_no}`} empty={text.noAlerts} />
          </Box>
        </Collapse>
      </Paper>

      {/* Filters */}
      <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mb: 2 }} alignItems="center">
        <TextField select size="small" label={text.status} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="">{text.all}</MenuItem>
          {STATUSES.map((s) => <MenuItem key={s} value={s}>{text.statuses[s]}</MenuItem>)}
        </TextField>
        <TextField select size="small" label={text.semenType} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="">{text.all}</MenuItem>
          {SEMEN_TYPES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <FormControlLabel control={<Switch checked={lowStock} onChange={(e) => setLowStock(e.target.checked)} />} label={text.lowStock} />
        <FormControlLabel control={<Switch checked={nearExpiry} onChange={(e) => setNearExpiry(e.target.checked)} />} label={text.nearExpiry} />
      </Stack>

      {/* Stock list */}
      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <TableContainer sx={smartTableContainerSx}>
          <Table sx={{ ...smartTableSx, minWidth: 1040 }}>
            <TableHead sx={smartTableHeadSx}>
              <TableRow>
                <TableCell>{text.stallion}</TableCell>
                <TableCell>{text.batchNo}</TableCell>
                <TableCell align="center">{text.semenType}</TableCell>
                <TableCell align="center">{text.sourceType}</TableCell>
                <TableCell align="center">{text.total}</TableCell>
                <TableCell align="center">{text.used}</TableCell>
                <TableCell align="center">{text.reserved}</TableCell>
                <TableCell align="center">{text.remaining}</TableCell>
                <TableCell>{text.storage}</TableCell>
                <TableCell align="center">{text.expiryDate}</TableCell>
                <TableCell align="center">{text.status}</TableCell>
                <TableCell align="center">{text.actions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.map((b) => (
                <TableRow key={b.batch_id} hover sx={{ cursor: 'pointer' }} onClick={() => openCard(b)}>
                  <TableCell sx={{ fontWeight: 700 }}>{b.stallion_name || '-'}</TableCell>
                  <TableCell>{b.batch_no}</TableCell>
                  <TableCell align="center">{b.semen_type}</TableCell>
                  <TableCell align="center">{text.sources[b.source_type] || b.source_type}</TableCell>
                  <TableCell align="center">{b.doses_total}</TableCell>
                  <TableCell align="center">{b.doses_used}</TableCell>
                  <TableCell align="center">{b.doses_reserved}</TableCell>
                  <TableCell align="center"><b style={{ color: b.doses_remaining <= 2 ? COLORS.rose : COLORS.text }}>{b.doses_remaining}</b></TableCell>
                  <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fmtStorage(b)}</TableCell>
                  <TableCell align="center">{(b.expiry_date || '').slice(0, 10) || '-'}</TableCell>
                  <TableCell align="center"><StatusChip s={b.status} /></TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title={text.edit}><IconButton size="small" sx={smartActionButtonSx(COLORS.blue, COLORS.blueSoft)} onClick={() => openEdit(b)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title={text.del}><IconButton size="small" sx={smartActionButtonSx(COLORS.rose, COLORS.roseSoft)} onClick={() => setDelTarget(b)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRow><TableCell colSpan={12} align="center"><CircularProgress size={26} sx={{ my: 3 }} /></TableCell></TableRow>}
              {!loading && rows.length === 0 && <TableRow><TableCell colSpan={12} align="center" sx={{ py: 4, color: COLORS.muted }}>{text.noData}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit batch dialog */}
      <Dialog open={batchOpen} onClose={() => setBatchOpen(false)} fullWidth maxWidth="md" dir={dir}>
        <DialogTitle sx={{ fontWeight: 900 }}>{editing ? text.editBatchTitle : text.addBatchTitle}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <TextField select label={text.stallion} value={batchForm.stallion_id} onChange={(e) => onPickStallion(e.target.value)}>
              <MenuItem value="">—</MenuItem>
              {stallions.map((s) => <MenuItem key={s.stallion_id} value={s.stallion_id}>{s.name}</MenuItem>)}
            </TextField>
            <TextField label={text.batchNo} value={batchForm.batch_no} onChange={(e) => setBatchForm({ ...batchForm, batch_no: e.target.value })} required />
            <TextField select label={text.sourceType} value={batchForm.source_type} onChange={(e) => setBatchForm({ ...batchForm, source_type: e.target.value })}>
              {Object.keys(text.sources).map((k) => <MenuItem key={k} value={k}>{text.sources[k]}</MenuItem>)}
            </TextField>
            <TextField select label={text.semenType} value={batchForm.semen_type} onChange={(e) => setBatchForm({ ...batchForm, semen_type: e.target.value })}>
              {SEMEN_TYPES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            {!editing && (
              <TextField type="number" label={text.initialDoses} value={batchForm.doses_total} onChange={(e) => setBatchForm({ ...batchForm, doses_total: e.target.value })} inputProps={{ min: 0 }} />
            )}
            <TextField type="date" label={text.collectionDate} InputLabelProps={{ shrink: true }} value={batchForm.collection_date} onChange={(e) => setBatchForm({ ...batchForm, collection_date: e.target.value })} />
            <TextField type="date" label={text.receiveDate} InputLabelProps={{ shrink: true }} value={batchForm.receive_date} onChange={(e) => setBatchForm({ ...batchForm, receive_date: e.target.value })} />
            <TextField type="date" label={text.expiryDate} InputLabelProps={{ shrink: true }} value={batchForm.expiry_date} onChange={(e) => setBatchForm({ ...batchForm, expiry_date: e.target.value })} />
            <TextField label={text.tank} value={batchForm.storage_tank} onChange={(e) => setBatchForm({ ...batchForm, storage_tank: e.target.value })} />
            <TextField label={text.canister} value={batchForm.storage_canister} onChange={(e) => setBatchForm({ ...batchForm, storage_canister: e.target.value })} />
            <TextField label={text.cane} value={batchForm.storage_cane} onChange={(e) => setBatchForm({ ...batchForm, storage_cane: e.target.value })} />
            <TextField label={text.goblet} value={batchForm.storage_goblet} onChange={(e) => setBatchForm({ ...batchForm, storage_goblet: e.target.value })} />
            <TextField label={text.notes} value={batchForm.notes} onChange={(e) => setBatchForm({ ...batchForm, notes: e.target.value })} multiline sx={{ gridColumn: { sm: '1 / -1' } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBatchOpen(false)}>{text.cancel}</Button>
          <Button variant="contained" onClick={saveBatch} sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover } }}>{text.save}</Button>
        </DialogActions>
      </Dialog>

      {/* Batch card dialog */}
      <Dialog open={cardOpen} onClose={() => setCardOpen(false)} fullWidth maxWidth="md" dir={dir}>
        {card && (
          <>
            <DialogTitle sx={{ fontWeight: 900 }}>
              {text.batchCard} — {card.stallion_name || '-'} · {card.batch_no} <StatusChip s={card.status} />
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
                <Metric label={text.total} value={card.doses_total} />
                <Metric label={text.used} value={card.doses_used} />
                <Metric label={text.reserved} value={card.doses_reserved} />
                <Metric label={text.remaining} value={card.doses_remaining} highlight={card.doses_remaining <= 2} />
                <Metric label={text.semenType} value={card.semen_type} />
                <Metric label={text.sourceType} value={text.sources[card.source_type] || card.source_type} />
                <Metric label={text.expiryDate} value={(card.expiry_date || '').slice(0, 10) || '-'} />
                <Metric label={text.storage} value={fmtStorage(card)} />
              </Box>

              {!['Disposed', 'Expired'].includes(card.status) && (
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                  {MOVEMENT_TYPES.map((mt) => (
                    <Button key={mt} size="small" variant="outlined" onClick={() => openMovement(mt)}
                      sx={{ fontWeight: 800, borderColor: COLORS.line, color: COLORS.text }}>
                      {text.movementTypes[mt]}
                    </Button>
                  ))}
                </Stack>
              )}

              <Typography sx={{ fontWeight: 900, color: COLORS.text, mb: 1 }}>{text.movements}</Typography>
              <TableContainer sx={{ ...smartTableContainerSx, maxHeight: 320 }}>
                <Table size="small" stickyHeader sx={smartTableSx}>
                  <TableHead sx={smartTableHeadSx}>
                    <TableRow>
                      <TableCell>{text.movementType}</TableCell>
                      <TableCell align="center">{text.quantity}</TableCell>
                      <TableCell align="center">{text.remaining}</TableCell>
                      <TableCell align="center">{text.caseId}</TableCell>
                      <TableCell align="center">{text.movementDate}</TableCell>
                      <TableCell>{text.reason}</TableCell>
                      <TableCell>{text.stallion}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(card.movements || []).map((m) => (
                      <TableRow key={m.movement_id} hover>
                        <TableCell><Chip size="small" label={text.movementTypes[m.movement_type] || m.movement_type} sx={{ fontWeight: 700 }} /></TableCell>
                        <TableCell align="center">{m.quantity}</TableCell>
                        <TableCell align="center">{m.remaining_after}</TableCell>
                        <TableCell align="center">{m.case_id || '-'}</TableCell>
                        <TableCell align="center">{(m.movement_date || '').slice(0, 16).replace('T', ' ')}</TableCell>
                        <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.reason || '-'}</TableCell>
                        <TableCell>{m.performed_by_name || '-'}</TableCell>
                      </TableRow>
                    ))}
                    {(!card.movements || card.movements.length === 0) && (
                      <TableRow><TableCell colSpan={7} align="center" sx={{ color: COLORS.muted, py: 3 }}>{text.noMovements}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setCardOpen(false)}>{text.close}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Movement dialog */}
      <Dialog open={moveOpen} onClose={() => setMoveOpen(false)} fullWidth maxWidth="sm" dir={dir}>
        <DialogTitle sx={{ fontWeight: 900 }}>{text.addMovement} — {text.movementTypes[moveForm.movement_type]}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <TextField select label={text.movementType} value={moveForm.movement_type} onChange={(e) => setMoveForm({ ...moveForm, movement_type: e.target.value })}>
              {MOVEMENT_TYPES.map((mt) => <MenuItem key={mt} value={mt}>{text.movementTypes[mt]}</MenuItem>)}
            </TextField>
            {needsQty && (
              <TextField type="number" label={text.quantity} value={moveForm.quantity} onChange={(e) => setMoveForm({ ...moveForm, quantity: e.target.value })}
                inputProps={{ step: 1 }} helperText={moveForm.movement_type === 'Adjust' ? '+/-' : ''} />
            )}
            {['Reserve', 'Release', 'Use'].includes(moveForm.movement_type) && (
              <TextField label={text.caseId} value={moveForm.case_id} onChange={(e) => setMoveForm({ ...moveForm, case_id: e.target.value })} />
            )}
            {moveForm.movement_type === 'Use' && (
              <TextField label={text.inseminationId} value={moveForm.insemination_id} onChange={(e) => setMoveForm({ ...moveForm, insemination_id: e.target.value })} />
            )}
            {needsReason && (
              <TextField label={text.reason} value={moveForm.reason} onChange={(e) => setMoveForm({ ...moveForm, reason: e.target.value })} required sx={{ gridColumn: { sm: '1 / -1' } }} />
            )}
            <TextField type="datetime-local" label={text.movementDate} InputLabelProps={{ shrink: true }} value={moveForm.movement_date} onChange={(e) => setMoveForm({ ...moveForm, movement_date: e.target.value })} />
            {moveForm.movement_type === 'Use' && (
              <FormControlLabel control={<Switch checked={moveForm.from_reservation} onChange={(e) => setMoveForm({ ...moveForm, from_reservation: e.target.checked })} />} label={text.fromReservation} />
            )}
            <TextField label={text.notes} value={moveForm.notes} onChange={(e) => setMoveForm({ ...moveForm, notes: e.target.value })} multiline sx={{ gridColumn: { sm: '1 / -1' } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMoveOpen(false)}>{text.cancel}</Button>
          <Button variant="contained" onClick={saveMovement} sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover } }}>{text.save}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!delTarget} onClose={() => setDelTarget(null)} dir={dir}>
        <DialogTitle sx={{ fontWeight: 900 }}>{text.confirmDelete}</DialogTitle>
        <DialogContent><Typography sx={{ color: COLORS.muted }}>{text.deleteHint}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDelTarget(null)}>{text.cancel}</Button>
          <Button color="error" variant="contained" onClick={doDelete}>{text.del}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Metric = ({ label, value, highlight }) => (
  <Paper elevation={0} sx={{ ...cardSx, p: 1.5 }}>
    <Typography variant="caption" sx={{ color: COLORS.muted }}>{label}</Typography>
    <Typography sx={{ fontWeight: 900, color: highlight ? COLORS.rose : COLORS.text, fontSize: 18 }}>{value}</Typography>
  </Paper>
);

const AlertColumn = ({ title, items, color, render, empty }) => (
  <Box>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Box sx={{ width: 6, height: 18, borderRadius: 1, bgcolor: color }} />
      <Typography sx={{ fontWeight: 900, color: COLORS.text }}>{title}</Typography>
      <Chip size="small" label={items?.length || 0} sx={{ height: 20, fontWeight: 800 }} />
    </Stack>
    {(!items || items.length === 0) && <Typography variant="caption" sx={{ color: COLORS.muted }}>{empty}</Typography>}
    <Stack spacing={0.5}>
      {(items || []).slice(0, 6).map((x) => (
        <Typography key={x.batch_id} variant="body2" sx={{ color: COLORS.text }}>• {render(x)}</Typography>
      ))}
    </Stack>
  </Box>
);

export default SemenInventory;
